import { useEffect, useRef, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export type ViolationAction = "warn" | "pause_only" | "revoke_enrollment";

export interface PlayerSecuritySettings {
  block_devtools?: boolean;
  block_right_click?: boolean;
  block_copy?: boolean;
  block_screenshot?: boolean;
  tab_switch_pause?: boolean;
  tab_switch_flag?: boolean;
  tab_switch_threshold?: number;
  tab_switch_window_seconds?: number;
  legal_warning_enabled?: boolean;
  max_devices_per_user?: number;
  block_vm_emulator?: boolean;
  block_multi_monitor?: boolean;
  violation_action?: ViolationAction;
}

interface VideoProtectionOptions {
  userEmail?: string;
  userId: string;
  videoRef: React.RefObject<HTMLVideoElement>;
  containerRef?: React.RefObject<HTMLDivElement>;
  lessonId?: string;
  enabled: boolean;
  onAccessRevoked?: () => void;
  /** Per-course/per-group security configuration — when omitted, all protections are ON. */
  securitySettings?: PlayerSecuritySettings;
}

const DEFAULTS: Required<Omit<PlayerSecuritySettings, "violation_action">> & { violation_action: ViolationAction } = {
  block_devtools: true,
  block_right_click: true,
  block_copy: true,
  block_screenshot: true,
  tab_switch_pause: true,
  tab_switch_flag: true,
  tab_switch_threshold: 3,
  tab_switch_window_seconds: 30,
  legal_warning_enabled: true,
  max_devices_per_user: 2,
  block_vm_emulator: true,
  block_multi_monitor: true,
  violation_action: "revoke_enrollment",
};

const reportAttempt = async (attemptType: string): Promise<{ revoked?: boolean; violationNumber?: number; threshold?: number }> => {
  try {
    const res = await supabase.functions.invoke("manage-certificates", {
      body: { action: "report_download_attempt", attemptType },
    });
    return res.data || {};
  } catch {
    return {};
  }
};

const updateSession = async (sessionId: string): Promise<{ revoked?: boolean }> => {
  try {
    const res = await supabase.functions.invoke("manage-certificates", {
      body: { action: "update_session", sessionId },
    });
    return res.data || {};
  } catch (err) {
    console.error("Session update failed:", err);
    return {};
  }
};

export const useVideoProtection = ({
  userEmail, userId, videoRef, containerRef, enabled, onAccessRevoked, securitySettings,
}: VideoProtectionOptions) => {
  const { toast } = useToast();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const devToolsWarned = useRef(false);
  const reportedRef = useRef<Set<string>>(new Set());
  const sessionIdRef = useRef<string | null>(null);

  // Resolve final settings (admin overrides → fall back to "all on" defaults)
  const s: typeof DEFAULTS = { ...DEFAULTS, ...(securitySettings || {}) };
  const violationMode: ViolationAction = (s.violation_action as ViolationAction) || "revoke_enrollment";

  const showWarningAndReport = useCallback(async (type: string, title: string, description: string) => {
    if (videoRef.current) videoRef.current.pause();

    if (reportedRef.current.has(type)) return;
    reportedRef.current.add(type);

    // pause_only mode: just pause + local toast, don't report or revoke
    if (violationMode === "pause_only") {
      toast({ title, description, variant: "destructive" });
      return;
    }

    const result = await reportAttempt(type);
    const vNum = result.violationNumber;
    const threshold = result.threshold;

    // Only the "revoke_enrollment" mode actually revokes; "warn" mode keeps user in
    if (violationMode === "revoke_enrollment" && result.revoked && onAccessRevoked) {
      toast({
        title: "🚫 Access Revoked",
        description: "Repeated policy violations detected. Your course access has been revoked. You are being redirected.",
        variant: "destructive",
      });
      setTimeout(() => onAccessRevoked(), 2500);
    } else {
      toast({
        title,
        description: vNum && threshold && violationMode === "revoke_enrollment"
          ? `${description} (Warning ${vNum} of ${threshold} — next violation will revoke your access.)`
          : description,
        variant: "destructive",
      });
    }
  }, [toast, videoRef, onAccessRevoked, violationMode]);

  // ── Keyboard shortcuts (screenshot, copy/view-source, devtools)
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (s.block_screenshot && (e.key === "PrintScreen" || e.keyCode === 44)) {
      e.preventDefault();
      showWarningAndReport("screenshot_attempt", "🚫 Screenshot Detected", "Screenshots are strictly prohibited during course playback.");
      return;
    }

    if (
      s.block_screenshot && (
        (e.shiftKey && e.metaKey && (e.key === "s" || e.key === "S")) ||
        (e.altKey && e.metaKey && (e.key === "s" || e.key === "S"))
      )
    ) {
      e.preventDefault();
      e.stopPropagation();
      showWarningAndReport("snip_capture", "🚫 Screen Capture Blocked", "Snipping/capture shortcuts are not allowed during course playback.");
      return;
    }

    // F12 + DevTools shortcuts (gated by block_devtools)
    if (
      s.block_devtools && (
        e.key === "F12" ||
        (e.ctrlKey && e.shiftKey && ["I", "J", "C"].includes(e.key)) ||
        (e.metaKey && e.altKey && ["i", "I", "j", "J", "c", "C"].includes(e.key))
      )
    ) {
      e.preventDefault();
      e.stopPropagation();
      showWarningAndReport("keyboard_shortcut", "⚠️ Action Blocked", "DevTools shortcuts are not allowed during playback.");
      return;
    }

    // Copy / View source (gated by block_copy)
    if (
      s.block_copy &&
      e.ctrlKey && ["u", "U", "s", "S", "c", "C"].includes(e.key)
    ) {
      e.preventDefault();
      e.stopPropagation();
      showWarningAndReport("keyboard_shortcut", "⚠️ Action Blocked", "Copy / Save / View Source is not allowed during playback.");
    }
  }, [showWarningAndReport, s.block_screenshot, s.block_devtools, s.block_copy]);

  const handleKeyUp = useCallback((e: KeyboardEvent) => {
    if (s.block_screenshot && (e.key === "PrintScreen" || e.keyCode === 44)) {
      showWarningAndReport("screenshot_attempt", "🚫 Screenshot Detected", "Screenshots are strictly prohibited during course playback.");
    }
  }, [showWarningAndReport, s.block_screenshot]);

  // DevTools detection via debugger timing + VM/headless heuristics
  useEffect(() => {
    if (!enabled) return;
    if (!s.block_devtools && !s.block_vm_emulator) return;

    const detectDevTools = () => {
      if (!s.block_devtools) return;
      const threshold = 160;
      const start = performance.now();
      try {
        const check = new Function("debugger");
        check();
      } catch { /* ignore */ }
      const end = performance.now();
      if (end - start > threshold && !devToolsWarned.current) {
        devToolsWarned.current = true;
        if (videoRef.current) videoRef.current.pause();
        showWarningAndReport("devtools_open", "⚠️ Developer Tools Detected", "Video paused. Developer tools are not allowed during course playback.");
        setTimeout(() => { devToolsWarned.current = false; }, 10000);
      }
    };

    const detectVM = () => {
      if (!s.block_vm_emulator) return;
      try {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        if (gl) {
          const debugInfo = (gl as WebGLRenderingContext).getExtension('WEBGL_debug_renderer_info');
          if (debugInfo) {
            const renderer = String((gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || '');
            const vendor = String((gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || '');
            const vmKeywords = ['swiftshader', 'llvmpipe', 'virtualbox', 'vmware', 'software', 'virtual', 'parallels', 'qemu', 'microsoft basic render'];
            const isVM = vmKeywords.some(kw => renderer.toLowerCase().includes(kw) || vendor.toLowerCase().includes(kw));
            if (isVM) showWarningAndReport("vm_detected", "🚫 Virtual Machine Blocked", "Course playback is disabled inside virtual environments/VMs for security.");
          }
        }
      } catch (err) { console.error('VM check error:', err); }
    };

    const detectHeadless = () => {
      if (!s.block_vm_emulator) return;
      if (navigator.webdriver) {
        showWarningAndReport("headless_browser_detected", "🚫 Automated Tool Blocked", "Automated browsers or scraping tools are not allowed.");
      }
    };

    const interval = setInterval(() => {
      detectDevTools();
      detectVM();
      detectHeadless();
    }, 4000);

    detectVM();
    detectHeadless();

    return () => clearInterval(interval);
  }, [enabled, videoRef, showWarningAndReport, s.block_devtools, s.block_vm_emulator]);

  // Device limit check — controlled by max_devices_per_user (1+)
  useEffect(() => {
    if (!enabled || !userId) return;
    const maxDevices = Math.max(1, Number(s.max_devices_per_user) || 2);

    let tabSessionId = sessionStorage.getItem("fitawrari_session_id");
    if (!tabSessionId) {
      tabSessionId = crypto.randomUUID();
      sessionStorage.setItem("fitawrari_session_id", tabSessionId);
    }
    sessionIdRef.current = tabSessionId;

    const ping = async () => {
      const result = await updateSession(tabSessionId!);
      if (result.revoked && onAccessRevoked && violationMode === "revoke_enrollment") {
        toast({
          title: "🚫 Device Limit Exceeded",
          description: `You are signed in on more than ${maxDevices} devices. Access has been revoked.`,
          variant: "destructive",
        });
        setTimeout(() => onAccessRevoked(), 2000);
      }
    };

    ping();
    const sessionInterval = setInterval(ping, 30000);
    return () => clearInterval(sessionInterval);
  }, [enabled, userId, onAccessRevoked, toast, s.max_devices_per_user, violationMode]);

  // MediaRecorder & getDisplayMedia API interception (gated by block_screenshot)
  useEffect(() => {
    if (!enabled || !s.block_screenshot) return;

    const OriginalMediaRecorder = window.MediaRecorder;
    if (OriginalMediaRecorder) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).MediaRecorder = class FakeMediaRecorder {
          constructor() {
            showWarningAndReport("screen_recording", "⚠️ Screen Recording Blocked", "Screen recording APIs are not allowed during course playback.");
            if (videoRef.current) videoRef.current.pause();
            throw new Error("MediaRecorder is disabled for content protection.");
          }
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).MediaRecorder.isTypeSupported = () => false;
      } catch { /* ignore */ }
    }

    if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
      const originalGetDisplayMedia = navigator.mediaDevices.getDisplayMedia;
      navigator.mediaDevices.getDisplayMedia = async () => {
        showWarningAndReport("screen_share_attempt", "🚫 Screen Sharing Blocked", "Screen sharing is not allowed during course playback.");
        throw new Error("Screen sharing is disabled for content protection.");
      };
      // restore original on cleanup
      return () => {
        try { navigator.mediaDevices.getDisplayMedia = originalGetDisplayMedia; } catch { /* ignore */ }
        if (OriginalMediaRecorder) {
          try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window as any).MediaRecorder = OriginalMediaRecorder;
          } catch { /* ignore */ }
        }
      };
    }

    return () => {
      if (OriginalMediaRecorder) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (window as any).MediaRecorder = OriginalMediaRecorder;
        } catch { /* ignore */ }
      }
    };
  }, [enabled, videoRef, showWarningAndReport, s.block_screenshot]);

  // Block right-click + key handlers + downloader/OBS scavenger
  useEffect(() => {
    if (!enabled) return;
    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("keyup", handleKeyUp, true);

    const handleResize = () => {
      if (!s.block_devtools) return;
      const threshold = 160;
      if (window.outerWidth - window.innerWidth > threshold || window.outerHeight - window.innerHeight > threshold) {
        showWarningAndReport("devtools_docked", "⚠️ DevTools Detected", "Developer tools are not allowed during course playback.");
      }
    };
    window.addEventListener("resize", handleResize);

    const blockContextMenu = (e: Event) => { e.preventDefault(); };
    if (s.block_right_click) {
      window.addEventListener("contextmenu", blockContextMenu, true);
    }

    let downloaderInterval: number | null = null;
    if (s.block_screenshot) {
      // OBS / IDM / downloader scavenger only runs when block_screenshot is on
      downloaderInterval = window.setInterval(() => {
        const suspiciousSelectors = [
          '#idm_browser_extension_overlay',
          '[id*="idm-"]',
          '[class*="idm-"]',
          '.idm_download_button',
          '#idm_download_button',
          '[id*="download-helper"]',
          '[class*="download-helper"]',
          '[id*="obs-"]',
          '[class*="obs-"]',
        ];
        for (const selector of suspiciousSelectors) {
          if (document.querySelector(selector)) {
            showWarningAndReport("downloader_tool_detected", "🚫 Recording/Download Tool Detected", "OBS, IDM, or a similar tool was detected. Please disable it to continue.");
            break;
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (typeof (window as any).obsstudio !== "undefined") {
          showWarningAndReport("obs_browser_source", "🚫 OBS Detected", "OBS Studio Browser Source is not allowed during course playback.");
        }
        const ua = navigator.userAgent.toLowerCase();
        if (ua.includes("obs") || ua.includes("xsplit") || ua.includes("streamlabs")) {
          showWarningAndReport("streaming_software_detected", "🚫 Streaming Software Detected", "OBS / XSplit / Streamlabs is not allowed while watching courses.");
        }
      }, 2000);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("keyup", handleKeyUp, true);
      window.removeEventListener("resize", handleResize);
      if (s.block_right_click) {
        window.removeEventListener("contextmenu", blockContextMenu, true);
      }
      if (downloaderInterval !== null) clearInterval(downloaderInterval);
    };
  }, [enabled, handleKeyDown, handleKeyUp, showWarningAndReport, s.block_devtools, s.block_right_click, s.block_screenshot]);

  // Tab/window focus switching detection (controlled by tab_switch_pause + tab_switch_flag)
  useEffect(() => {
    if (!enabled) return;
    if (!s.tab_switch_pause && !s.tab_switch_flag) return;

    const switches: number[] = [];
    const WINDOW_MS = (s.tab_switch_window_seconds || 30) * 1000;
    const THRESHOLD = Math.max(1, s.tab_switch_threshold || 3);

    const recordSwitch = () => {
      const now = Date.now();
      switches.push(now);
      while (switches.length && switches[0] < now - WINDOW_MS) switches.shift();

      // Pause on switch (gentle UX)
      if (s.tab_switch_pause && videoRef.current && !videoRef.current.paused) {
        videoRef.current.pause();
      }

      if (s.tab_switch_flag && switches.length >= THRESHOLD) {
        switches.length = 0;
        showWarningAndReport(
          "rapid_focus_switching",
          "🚫 Suspicious Activity",
          "Rapid tab/window switching detected during playback — this is often associated with screen recording.",
        );
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") recordSwitch();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", recordSwitch);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", recordSwitch);
    };
  }, [enabled, videoRef, showWarningAndReport, s.tab_switch_pause, s.tab_switch_flag, s.tab_switch_threshold, s.tab_switch_window_seconds]);

  // Multi-monitor detection (gated by block_multi_monitor)
  useEffect(() => {
    if (!enabled || !s.block_multi_monitor) return;

    const checkScreens = async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const isExtended = (window.screen as any)?.isExtended;
        if (isExtended === true) {
          showWarningAndReport(
            "multi_monitor_detected",
            "🚫 Multiple Displays Detected",
            "External monitors are not allowed during paid course playback. Disconnect secondary displays to continue.",
          );
        }
      } catch { /* ignore */ }
    };

    checkScreens();
    const id = setInterval(checkScreens, 8000);
    return () => clearInterval(id);
  }, [enabled, showWarningAndReport, s.block_multi_monitor]);

  return {
    canvasRef,
    isProtected: enabled,
    watermarkText: `${userEmail || "Student"} | ${userId ? userId.slice(0, 8) : "Unknown"}`,
    requiresLegalWarning: !!s.legal_warning_enabled,
  };
};
